import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import verifiedSuppliersBanner from '../../assets/images/banners/verified-suppliers.webp';
import multipleSellersBanner from '../../assets/images/banners/multiple-sellers.webp';
import growBusinessBanner from '../../assets/images/banners/grow-business.webp';
import { useLocalizedPath } from '../../contexts/LanguageContext';
import { useHomeSections } from './HomeSectionsContext';
import './InteractiveBanner.css';

const InteractiveBanner = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const localizedPath = useLocalizedPath();
  const { available, requestTab } = useHomeSections();

  // Each link only appears once its section is known to have content, so it
  // never points at a section that isn't on the page.
  const quickLinks = [
    { key: 'deals', label: 'Discounts!', href: '#hot-deals', tab: 'deals' },
    { key: 'bestSellers', label: 'Most popular', href: '#best-sellers', tab: 'bestSellers' },
    { key: 'topSellers', label: 'Top Sellers', href: '#top-sellers' },
    { key: 'recent', label: 'Just Added', href: '#just-added', tab: 'recent' },
    { key: 'auctions', label: 'Auctions', href: '#latest-auctions', tab: 'auctions' },
    { key: 'faqs', label: 'FAQs', href: '#faqs', always: true },
  ].filter((link) => link.always || available[link.key]);

  const goTo = (link) => {
    if (link.startsWith('#')) {
      document.querySelector(link)?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    navigate(localizedPath(link));
  };

  const slides = [
    {
      id: 1,
      title: "Buy From Verified Suppliers",
      subtitle: "Explore products for your business, compare sellers and purchase with shipping and taxes confirmed before payment.",
      image: verifiedSuppliersBanner,
      buttonText: "Shop Products",
      buttonLink: "/search",
      secondaryButtonText: "Become a Seller",
      secondaryButtonLink: "/sell/register",
      bgColor: "slide-bg-blue"
    },
        {
      id: 2,
      title: "Shop From Multiple Sellers",
      subtitle: "Purchase from different suppliers in one place while tracking each seller's products, delivery date and fulfillment status.",
      image: multipleSellersBanner,
      buttonText: "See How It Works",
      buttonLink: "#how-eulua-works",
      secondaryButtonText: "Browse Products",
      secondaryButtonLink: "/search",
      bgColor: "slide-bg-green"
    },
    {
      id: 3,
      title: "Grow Your Business on Eulua",
      subtitle: "Apply as a seller, publish your catalog and reach verified buyers with clear commercial terms.",
      image: growBusinessBanner,
      buttonText: "Become a Seller",
      buttonLink: "/sell/register",
      secondaryButtonText: "Explore the Marketplace",
      secondaryButtonLink: "/search",
      bgColor: "slide-bg-gray"
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleSlideClick = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className="banner-container">
      {/* Hero Banner Carousel */}
      <div className="carousel-container">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`carousel-slide ${index === currentSlide ? 'active' : ''} ${slide.bgColor}`}
            aria-hidden={index !== currentSlide}
          >
            <div className="slide-content">
              <div className="slide-grid">
                <div className="slide-text">
                  {index === 0 ? <h1 className="slide-title">
                    {slide.title}
                  </h1> : <h2 className="slide-title">{slide.title}</h2>}
                  <p className="slide-subtitle">
                    {slide.subtitle}
                  </p>
                  <div className="slide-actions">
                    <button className="slide-button" onClick={() => goTo(slide.buttonLink)}>
                      {slide.buttonText}
                    </button>
                    <button className="slide-button slide-button-secondary" onClick={() => goTo(slide.secondaryButtonLink)}>
                      {slide.secondaryButtonText}
                    </button>
                  </div>
                </div>
                <div className="slide-image-container">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="slide-image"
                    width="1280"
                    height="720"
                    decoding="async"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchpriority={index === 0 ? 'high' : 'low'}
                  />
                  <div className="slide-overlay"></div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="nav-arrow nav-arrow-left"
          aria-label="Previous promotion"
        >
          <ChevronLeft className="arrow-icon" />
        </button>
        <button
          onClick={nextSlide}
          className="nav-arrow nav-arrow-right"
          aria-label="Next promotion"
        >
          <ChevronRight className="arrow-icon" />
        </button>

        {/* Slide Indicators */}
        <div className="slide-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => handleSlideClick(index)}
              className={`indicator ${index === currentSlide ? 'indicator-active' : ''}`}
              aria-label={`Show promotion ${index + 1}`}
              aria-current={index === currentSlide ? 'true' : undefined}
            />
          ))}
        </div>
      </div>

      {/* Quick Access Bar */}
      <div className="quick-access">
        <div className="quick-access-container">
          <div className="quick-access-content">
            <div className="quick-links">
              <span className="quick-label">Quick Access:</span>
              {quickLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  className="quick-link"
                  onClick={link.tab ? (event) => {
                    event.preventDefault();
                    requestTab(link.tab);
                  } : undefined}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveBanner;
